import { motion } from "framer-motion";
import { Zap, Link2, TrendingUp, DollarSign, ArrowRight, Radio, Building2, MapPin } from "lucide-react";
import { useState, useEffect } from "react";

// Animated compatibility score counter
const AnimatedScore = ({ target, delay = 0 }: { target: number; delay?: number }) => {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    if (!started) return;
    let current = 0;
    const step = Math.ceil(target / 30);
    const interval = setInterval(() => {
      current += step;
      if (current >= target) {
        setCount(target);
        clearInterval(interval);
      } else {
        setCount(current);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [started, target]);

  return (
    <motion.span
      onViewportEnter={() => setStarted(true)}
      viewport={{ once: true }}
      className="font-display font-bold"
    >
      {count}%
    </motion.span>
  );
};

// Simulated property cards for the chain visualization
const miniProperties = [
  { title: "Casa em Campinas", type: "Casa", city: "Campinas", value: "R$ 850.000", color: "bg-accent/15 border-accent/30" },
  { title: "Apto em São Paulo", type: "Apartamento", city: "São Paulo", value: "R$ 920.000", color: "bg-primary/10 border-primary/30" },
  { title: "Casa em Valinhos", type: "Casa", city: "Valinhos", value: "R$ 780.000", color: "bg-accent/15 border-accent/30" },
];

const SwapEngineSection = () => {
  return (
    <section className="py-24 bg-muted/30 relative overflow-hidden">
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <div className="absolute top-20 left-20 w-96 h-96 rounded-full bg-accent blur-[120px]" />
        <div className="absolute bottom-20 right-20 w-80 h-80 rounded-full bg-primary blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-accent/10 text-accent text-sm font-display font-medium mb-6 border border-accent/20"
          >
            <Zap size={14} />
            Exclusivo na Permuta Digital
          </motion.div>

          <h2 className="text-3xl md:text-5xl font-display font-bold text-foreground mb-4 leading-tight">
            Motor de Permuta com
            <br />
            <span className="text-accent">Inteligência Artificial</span>
          </h2>
          <p className="text-muted-foreground font-body text-lg max-w-2xl mx-auto leading-relaxed">
            Nosso algoritmo de IA analisa automaticamente milhares de imóveis, calcula scores de compatibilidade
            e identifica oportunidades de permuta que você jamais encontraria sozinho.
          </p>
        </motion.div>

        {/* Feature 1: Compatibility Score */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
                <Zap className="text-accent" size={20} />
              </div>
              <span className="text-xs font-display font-bold text-accent uppercase tracking-wider">Score de Compatibilidade</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
              Cada imóvel recebe um score automático
            </h3>
            <p className="text-muted-foreground font-body leading-relaxed mb-6">
              Nosso algoritmo inteligente analisa <strong className="text-foreground">5 fatores</strong> com machine learning para calcular a compatibilidade entre dois imóveis:
              proximidade de valor, tipo de imóvel, localização desejada, metragem e intenção de troca.
            </p>
            <div className="space-y-3">
              {[
                { label: "Proximidade de valor", weight: "30%" },
                { label: "Localização desejada", weight: "25%" },
                { label: "Tipo de imóvel", weight: "20%" },
                { label: "Intenção de troca", weight: "15%" },
                { label: "Metragem compatível", weight: "10%" },
              ].map((factor, i) => (
                <motion.div
                  key={factor.label}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="flex items-center gap-3"
                >
                  <span className="text-xs font-display font-bold text-accent w-8">{factor.weight}</span>
                  <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      whileInView={{ width: `${parseInt(factor.weight)}%` }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.3 + i * 0.1, duration: 0.6 }}
                      className="h-full rounded-full bg-accent"
                      style={{ width: `${parseInt(factor.weight) * 3.3}%` }}
                    />
                  </div>
                  <span className="text-sm text-muted-foreground font-body min-w-[140px]">{factor.label}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* Animated score cards */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="space-y-4"
          >
            {[
              { score: 94, label: "Alta", title: "Apartamento em Pinheiros", city: "São Paulo", value: "R$ 1.200.000" },
              { score: 81, label: "Alta", title: "Casa em Alphaville", city: "Barueri", value: "R$ 1.050.000" },
              { score: 62, label: "Média", title: "Sobrado em Moema", city: "São Paulo", value: "R$ 980.000" },
            ].map((item, i) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 15, scale: 0.98 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 + i * 0.12 }}
                whileHover={{ scale: 1.02, y: -2 }}
                className="p-4 rounded-2xl bg-card border border-border/50 shadow-card flex items-center gap-4 cursor-default"
              >
                <div className="w-14 h-14 rounded-xl bg-muted flex items-center justify-center shrink-0">
                  <Building2 className="text-muted-foreground" size={22} />
                </div>
                <div className="flex-1 min-w-0">
                  <h4 className="font-display font-semibold text-foreground text-sm">{item.title}</h4>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <MapPin size={10} />{item.city} · {item.value}
                  </p>
                </div>
                <div className={`px-3 py-1.5 rounded-lg font-display font-bold text-sm ${
                  item.score >= 75
                    ? "bg-green-500/15 text-green-700 dark:text-green-400"
                    : "bg-amber-500/15 text-amber-700 dark:text-amber-400"
                }`}>
                  <AnimatedScore target={item.score} delay={i * 200} />
                </div>
              </motion.div>
            ))}

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.8 }}
              className="text-center text-sm text-muted-foreground font-body italic"
            >
              "Encontramos 8 imóveis com alta chance de permuta com o seu."
            </motion.p>
          </motion.div>
        </div>

        {/* Feature 2: Chain Swap */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-24">
          {/* Chain visualization */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="order-2 lg:order-1"
          >
            <div className="p-6 rounded-2xl bg-card border border-accent/20 shadow-card relative overflow-hidden">
              <div className="absolute top-0 right-0 w-32 h-32 bg-accent/5 rounded-full -translate-y-1/2 translate-x-1/2" />

              <div className="flex items-center gap-2 mb-5">
                <Link2 className="text-accent" size={18} />
                <span className="text-xs font-display font-bold text-accent uppercase tracking-wider">Permuta em Cadeia Detectada</span>
              </div>

              <div className="flex flex-col items-center gap-3">
                {miniProperties.map((prop, i) => (
                  <motion.div
                    key={prop.title}
                    initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: 0.3 + i * 0.15 }}
                    className="w-full"
                  >
                    <div className={`flex items-center gap-3 p-3 rounded-xl border ${prop.color}`}>
                      <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center shrink-0">
                        <Building2 size={16} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1">
                        <p className="text-sm font-display font-semibold text-foreground">{prop.title}</p>
                        <p className="text-[11px] text-muted-foreground">{prop.type} · {prop.value}</p>
                      </div>
                      <span className="text-[10px] font-display font-medium text-muted-foreground uppercase">{prop.city}</span>
                    </div>

                    {i < miniProperties.length - 1 && (
                      <motion.div
                        initial={{ opacity: 0, scaleY: 0 }}
                        whileInView={{ opacity: 1, scaleY: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: 0.5 + i * 0.15 }}
                        className="flex justify-center py-1"
                      >
                        <div className="flex flex-col items-center">
                          <div className="w-px h-3 bg-accent/40" />
                          <ArrowRight size={14} className="text-accent rotate-90" />
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                ))}

                {/* Closing the loop */}
                <motion.div
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1 }}
                  className="flex items-center gap-2 text-accent text-xs font-display font-semibold"
                >
                  <ArrowRight size={14} className="rotate-90" />
                  <span>volta ao primeiro imóvel — ciclo completo!</span>
                </motion.div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="order-1 lg:order-2"
          >
            <div className="flex items-center gap-2 mb-4">
              <div className="w-10 h-10 rounded-xl bg-accent/15 flex items-center justify-center">
                <Link2 className="text-accent" size={20} />
              </div>
              <span className="text-xs font-display font-bold text-accent uppercase tracking-wider">Permuta em Cadeia</span>
            </div>
            <h3 className="text-2xl md:text-3xl font-display font-bold text-foreground mb-4">
              Trocas que parecem impossíveis? Nós resolvemos.
            </h3>
            <p className="text-muted-foreground font-body leading-relaxed mb-4">
              Muitas permutas não acontecem porque exigem mais de duas partes. Nossa IA detecta
              <strong className="text-foreground"> automaticamente ciclos de troca</strong> envolvendo 3 ou mais imóveis usando algoritmos avançados de grafos.
            </p>
            <p className="text-muted-foreground font-body leading-relaxed mb-6">
              Esse conceito, usado em sistemas de troca de órgãos e plataformas internacionais de home swap,
              agora chega ao mercado imobiliário brasileiro.
            </p>
            <div className="p-4 rounded-xl bg-accent/5 border border-accent/20">
              <p className="text-sm font-body text-foreground">
                <strong>Exemplo real:</strong> Pessoa A quer apartamento, B quer casa maior, C quer casa menor.
                A troca direta não funciona — mas o ciclo A→B→C→A resolve tudo de uma vez.
              </p>
            </div>
          </motion.div>
        </div>

        {/* Bottom features grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            {
              icon: TrendingUp,
              title: "Índice de Liquidez",
              description: "Cada imóvel recebe um indicador de Alta, Média ou Baixa liquidez de permuta, baseado na quantidade de imóveis compatíveis na plataforma.",
            },
            {
              icon: DollarSign,
              title: "Simulador Financeiro",
              description: "Cálculo automático de diferença de valor, ITBI estimado e custos cartorários. Reduza a maior barreira da negociação.",
            },
            {
              icon: Radio,
              title: "Radar de Oportunidades",
              description: "Receba notificações em tempo real quando um novo imóvel compatível com o seu entra na plataforma. Nunca perca uma oportunidade.",
            },
          ].map((feature, i) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.1 }}
              whileHover={{ y: -4 }}
              className="p-6 rounded-2xl bg-card border border-border/50 shadow-card transition-shadow hover:shadow-card-hover"
            >
              <div className="w-12 h-12 rounded-xl bg-accent/15 flex items-center justify-center mb-4">
                <feature.icon className="text-accent" size={22} />
              </div>
              <h4 className="font-display font-bold text-foreground mb-2">{feature.title}</h4>
              <p className="text-sm text-muted-foreground font-body leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SwapEngineSection;
